import { cn } from '../../lib/utils'

export interface NoiseOverlayProps {
  /** Opacity of the noise texture (0-100) */
  opacity?: number
  /** Whether to blend with a gradient */
  withGradient?: boolean
  /** Custom className */
  className?: string
}

/**
 * Subtle noise texture overlay for adding depth to backgrounds
 * Gives a premium, slightly textured feel
 */
export function NoiseOverlay({
  opacity = 3,
  withGradient = false,
  className,
}: NoiseOverlayProps) {
  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-0 z-50',
        className
      )}
      style={{
        opacity: opacity / 100,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }}
    >
      {withGradient && (
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10" />
      )}
    </div>
  )
}

/**
 * Inline noise texture for specific elements
 * Not fixed, flows with content
 */
export function NoiseTexture({
  opacity = 5,
  className,
}: {
  opacity?: number
  className?: string
}) {
  return (
    <div
      className={cn('absolute inset-0 pointer-events-none', className)}
      style={{
        opacity: opacity / 100,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }}
    />
  )
}

/**
 * Grainy gradient background
 * Combines gradient with noise for a premium feel
 */
export function GrainyGradient({
  from = 'from-midnight-950',
  via = 'via-midnight-900',
  to = 'to-midnight-800',
  noiseOpacity = 4,
  className,
}: {
  from?: string
  via?: string
  to?: string
  noiseOpacity?: number
  className?: string
}) {
  return (
    <div className={cn('absolute inset-0', className)}>
      <div className={cn('absolute inset-0 bg-gradient-to-br', from, via, to)} />
      <NoiseTexture opacity={noiseOpacity} />
    </div>
  )
}
